// components/VideoEdit.tsx
// Edit existing video posts

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useFileUpload } from '../firebase/hooks';
import { useAuth } from '../contexts/AuthContext';
import { PostCategory, BarkBoardPost } from '../types';
import './VideoUpload.css';

export const VideoEdit: React.FC = () => {
    const { postId } = useParams<{ postId: string }>();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { uploadFile, uploadProgress, resetProgress } = useFileUpload();

    const [loading, setLoading] = useState(true);
    const [post, setPost] = useState<BarkBoardPost | null>(null);

    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [title, setTitle] = useState('');
    const [caption, setCaption] = useState('');
    const [category, setCategory] = useState<PostCategory>('writing-tips');
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Load existing post data
    useEffect(() => {
        const loadPost = async () => {
            if (!postId) {
                setError('Invalid post ID');
                setLoading(false);
                return;
            }

            try {
                const docRef = doc(db, 'barkBoard', 'posts', 'items', postId);
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    setError('Post not found');
                    setLoading(false);
                    return;
                }

                const data = docSnap.data();
                const loadedPost: BarkBoardPost = {
                    id: docSnap.id,
                    type: data.type || 'video',
                    title: data.title,
                    caption: data.caption,
                    category: data.category,
                    videoUrl: data.videoUrl,
                    thumbnailUrl: data.thumbnailUrl,
                    authorName: data.authorName,
                    authorEmail: data.authorEmail,
                    authorId: data.authorId || '',
                    createdAt: data.createdAt?.toDate(),
                    publishedAt: data.publishedAt?.toDate(),
                    status: data.status,
                    viewCount: data.viewCount,
                    complimentCount: data.complimentCount,
                };

                setPost(loadedPost);
                setTitle(data.title);
                setCaption(data.caption);
                setCategory(data.category);
                setLoading(false);
            } catch (err: any) {
                console.error('Failed to load post:', err);
                setError('Failed to load post');
                setLoading(false);
            }
        };

        loadPost();
    }, [postId]);

    const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('video/')) {
                setError('Please select a valid video file');
                return;
            }
            if (file.size > 100 * 1024 * 1024) {
                setError('Video file must be less than 100MB');
                return;
            }
            setVideoFile(file);
            setError('');
        }
    };

    const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setError('Thumbnail must be less than 5MB');
                return;
            }
            setThumbnailFile(file);
            setError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!title.trim() || !caption.trim()) {
            setError('Please fill in all required fields');
            return;
        }
        if (title.length < 5 || title.length > 100) {
            setError('Title must be between 5 and 100 characters');
            return;
        }

        setIsUpdating(true);
        setError('');

        try {
            let videoUrl = post?.videoUrl;
            let thumbnailUrl = post?.thumbnailUrl;

            // Upload new video if provided
            if (videoFile) {
                const videoPath = `barkBoard/videos/${Date.now()}_${videoFile.name}`;
                videoUrl = await uploadFile(videoFile, videoPath);
            }

            // Upload new thumbnail if provided
            if (thumbnailFile) {
                const thumbnailPath = `barkBoard/thumbnails/${Date.now()}_${thumbnailFile.name}`;
                thumbnailUrl = await uploadFile(thumbnailFile, thumbnailPath);
            }

            // Update post document in Firestore
            const updateData: any = {
                title: title.trim(),
                caption: caption.trim(),
                category,
                authorId: currentUser?.uid || '',
                updatedAt: serverTimestamp(),
                updatedBy: currentUser?.email || '',
            };

            if (videoUrl) updateData.videoUrl = videoUrl;
            if (thumbnailUrl) updateData.thumbnailUrl = thumbnailUrl;

            await updateDoc(doc(db, 'barkBoard', 'posts', 'items', postId!), updateData);

            setSuccess(true);
            resetProgress();

            setTimeout(() => {
                navigate('/content-library');
            }, 2000);

        } catch (err: any) {
            console.error('Update error:', err);
            setError(err.message || 'Failed to update video. Please try again.');
            setIsUpdating(false);
        }
    };

    const handleCancel = () => {
        navigate('/content-library');
    };

    if (loading) {
        return (
            <div className="upload-container">
                <div className="upload-card">
                    <p>Loading post...</p>
                </div>
            </div>
        );
    }

    if (error && !post) {
        return (
            <div className="upload-container">
                <div className="upload-card">
                    <div className="error-message">{error}</div>
                    <button onClick={handleCancel} className="cancel-button">
                        Back to Library
                    </button>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="upload-container">
                <div className="success-card">
                    <div className="success-icon">✅</div>
                    <h2>Video Updated Successfully!</h2>
                    <p>Your changes have been saved.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="upload-container">
            <div className="upload-card">
                <div className="upload-header">
                    <h1>Edit Video</h1>
                    <button onClick={handleCancel} className="close-button">×</button>
                </div>

                <form onSubmit={handleSubmit} className="upload-form">
                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    {/* Current Video Preview */}
                    {post?.videoUrl && !videoFile && (
                        <div className="form-section">
                            <label className="form-label">Current Video</label>
                            <div style={{ background: '#f7fafc', padding: '16px', borderRadius: '8px' }}>
                                <video
                                    src={post.videoUrl}
                                    controls
                                    style={{ width: '100%', maxHeight: '300px', borderRadius: '8px' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Replace Video (Optional) */}
                    <div className="form-section">
                        <label className="form-label">Replace Video (Optional)</label>
                        <div className="file-upload-area">
                            <input
                                type="file"
                                accept="video/*"
                                onChange={handleVideoChange}
                                disabled={isUpdating}
                                id="video-file"
                            />
                            <label htmlFor="video-file" className="file-upload-label">
                                {videoFile ? (
                                    <span>🎬 {videoFile.name}</span>
                                ) : (
                                    <span>Click to replace video file (MP4, MOV, AVI - Max 100MB)</span>
                                )}
                            </label>
                        </div>
                    </div>

                    {/* Current Thumbnail Preview */}
                    {post?.thumbnailUrl && !thumbnailFile && (
                        <div className="form-section">
                            <label className="form-label">Current Thumbnail</label>
                            <div style={{ background: '#f7fafc', padding: '16px', borderRadius: '8px' }}>
                                <img
                                    src={post.thumbnailUrl}
                                    alt="Current thumbnail"
                                    style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Replace Thumbnail (Optional) */}
                    <div className="form-section">
                        <label className="form-label">Replace Thumbnail (Optional)</label>
                        <div className="file-upload-area">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                                disabled={isUpdating}
                                id="thumbnail-file"
                            />
                            <label htmlFor="thumbnail-file" className="file-upload-label">
                                {thumbnailFile ? (
                                    <span>🖼️ {thumbnailFile.name}</span>
                                ) : (
                                    <span>Click to replace thumbnail (JPG, PNG - Max 5MB)</span>
                                )}
                            </label>
                        </div>
                    </div>

                    {/* Title */}
                    <div className="form-section">
                        <label className="form-label" htmlFor="title">Title *</label>
                        <input
                            type="text"
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., 5 Writing Tips for Beginners"
                            maxLength={100}
                            disabled={isUpdating}
                            className="text-input"
                        />
                        <span className="char-count">{title.length}/100</span>
                    </div>

                    {/* Caption */}
                    <div className="form-section">
                        <label className="form-label" htmlFor="caption">Caption *</label>
                        <textarea
                            id="caption"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder="Brief description of your video..."
                            rows={4}
                            disabled={isUpdating}
                            className="textarea-input"
                        />
                    </div>

                    {/* Category */}
                    <div className="form-section">
                        <label className="form-label" htmlFor="category">Category *</label>
                        <select
                            id="category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value as PostCategory)}
                            disabled={isUpdating}
                            className="select-input"
                        >
                            <option value="writing-tips">Writing Tips</option>
                            <option value="story-inspiration">Story Inspiration</option>
                            <option value="creative-tools">Creative Tools</option>
                            <option value="character-development">Character Development</option>
                            <option value="world-building">World Building</option>
                            <option value="community-spotlight">Community Spotlight</option>
                        </select>
                    </div>

                    {/* Upload Progress */}
                    {uploadProgress && uploadProgress.status === 'uploading' && (
                        <div className="progress-section">
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${uploadProgress.progress}%` }}
                                />
                            </div>
                            <p className="progress-text">
                                Uploading {uploadProgress.fileName}... {uploadProgress.progress}%
                            </p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="cancel-button"
                            disabled={isUpdating}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="publish-button"
                            disabled={isUpdating || !title || !caption}
                        >
                            {isUpdating ? 'Updating...' : 'Update Video'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};